/*
  This is an HOC used to wrap components that you wish to "trap focus" such that tabbing and alt-tabbing
  repeatedly will remain within the component. It will also grab focus when first mounted, and focus
  on either a specified element (3rd argument) or by default the 2nd focusable descendent. (skipping the usual close widget).
*/
import React, { useEffect, useLayoutEffect } from 'react';

import type { KeyboardHandlerEvent } from '@typings/utils.ts';

import { listenToShiftTab, listenToTab } from '../../../plugins/utils/keyboardUtils.ts';
import { isInIframe } from '../../utils/focus.ts';

type FocusableElement = HTMLElement & {
  checked?: boolean;
  name?: string;
  type?: string;
};

const findFocusableDescendents = (id: string) => {
  const element = id ? document.getElementById(id) : null;
  if (!element) {
    return [] as FocusableElement[];
  }

  return [
    ...element.querySelectorAll(
      'a[href]:not([disabled]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ] as FocusableElement[];
};

function limitTabFocusToDescendentsOf(id: string) {
  const removeTabListen = listenToTab(onKeyPressMoveFocusTrap(1, id), id);
  const removeShiftTabListen = listenToShiftTab(onKeyPressMoveFocusTrap(-1, id), id);

  return {
    removeTabListen,
    removeShiftTabListen,
  };
}

const onKeyPressMoveFocusTrap =
  (direction = 1, id: string) =>
  (e: KeyboardHandlerEvent) => {
    const descendents = findFocusableDescendents(id);
    if (descendents.length === 0) {
      return;
    }

    const activeElement = document.activeElement as FocusableElement | null;
    const isCurrentRadio = activeElement?.type === 'radio';
    const currentRadioName = isCurrentRadio ? activeElement?.name : null;

    let focusIndex = activeElement ? descendents.indexOf(activeElement) : -1;

    // some elements may be invisible or not focusable
    // try every element until one is focused
    for (let retry = 0; retry < descendents.length; retry++) {
      // when initial focusIndex is -1, the hidden logic is
      // when direction is 1, focusIndex + 1 = 0, first element
      // when direction is -1, focusIndex - 1 = -2, any negative value will jump to last element
      focusIndex += direction;
      if (focusIndex >= descendents.length) {
        focusIndex = 0;
      }
      if (focusIndex < 0) {
        focusIndex = descendents.length - 1;
      }
      let nextElem = descendents[focusIndex];

      if (nextElem.type === 'radio') {
        if (isCurrentRadio && currentRadioName === nextElem.name) {
          // don't tab into other radios in the same group
          continue;
        }

        if (!nextElem.checked) {
          // find the checked radio in this group
          const radiogroup = nextElem.name;
          for (let i = 0; i < descendents.length; i++) {
            const nextRadio = descendents[i];
            if (nextRadio.type === 'radio' && nextRadio.name === radiogroup && nextRadio.checked) {
              nextElem = nextRadio;
              focusIndex = i + direction;
              break;
            }
          }
        }
      } // use preventScroll when in an iframe. this work is the start of a refactor of how we handel focus while in an iframe
      nextElem.focus({ preventScroll: isInIframe() });
      if (document.activeElement === nextElem) {
        e.preventDefault();
        break;
      }
    }
    // we can't find any focusable element, fallback to default browser behavior
  };

const useFocusTrap = (focusTrapEnabled: boolean, id: string) => {
  useLayoutEffect(() => {
    const descendents = findFocusableDescendents(id);
    if (descendents.length) {
      if (!descendents.includes(document.activeElement as FocusableElement)) {
        // when a focused trapped component mounts prevent parent page from scrolling in iframe mode, this work is the start of a refactor of how we handel focus while in an iframe
        (descendents.length > 1 ? descendents[1] : descendents[0]).focus({ preventScroll: isInIframe() });
      }
    }

    let removeEventsOb: ReturnType<typeof limitTabFocusToDescendentsOf> | null = null;
    if (focusTrapEnabled) {
      removeEventsOb = limitTabFocusToDescendentsOf(id);
    }

    return () => {
      removeEventsOb?.removeTabListen();
      removeEventsOb?.removeShiftTabListen();
    };
  }, [focusTrapEnabled]);
};

const withFocusTrap =
  <P extends object>(WrappedComponent: React.ComponentType<P>, id: string, focusFirst?: string) =>
  (props: P & { focusTrapEnabled?: boolean }) => {
    const { focusTrapEnabled = true, ...others } = props;

    useFocusTrap(focusTrapEnabled, id);

    useEffect(() => {
      if (focusFirst) {
        // let the micro-task queue finish out in case the component needs to
        // be shown by the layer manager. also prevents parent page from scrolling in iframe mode
        setTimeout(
          () => (document.querySelector(focusFirst) as HTMLElement | null)?.focus({ preventScroll: isInIframe() }),
          0,
        );
      }
    }, []);

    return <WrappedComponent {...(others as P)} />;
  };

export { withFocusTrap, useFocusTrap };
